import type { PhotoRow } from '@/schema/estate';
import { readMedia } from './photoService';

export type UploadState = 'waiting' | 'uploading' | 'failed';
/** `checking`: this device's queue has not been listed yet, so absence means nothing. */
export type QueueState = UploadState | 'checking';
export type PhotoState = QueueState | 'uploaded' | 'missing';
export type UploadStatus = Readonly<Record<string, UploadState>>;

export function enqueued(s: UploadStatus, key: string): UploadStatus {
  return { ...s, [key]: 'waiting' };
}

export function listed(s: UploadStatus, keys: readonly string[]): UploadStatus {
  const next: Record<string, UploadState> = {};
  for (const key of keys) next[key] = s[key] ?? 'waiting';
  return next;
}

const failUploading = (s: UploadStatus): Record<string, UploadState> =>
  Object.fromEntries(Object.entries(s).map(([k, v]) => [k, v === 'uploading' ? 'failed' : v]));

// Drains run one at a time and entries upload in sequence, so a newer start means the
// previous upload did not commit.
export function started(s: UploadStatus, key: string): UploadStatus {
  return { ...failUploading(s), [key]: 'uploading' };
}

export function committed(s: UploadStatus, key: string): UploadStatus {
  return Object.fromEntries(Object.entries(s).filter(([k]) => k !== key));
}

export function drained(s: UploadStatus, failedKeys: readonly string[]): UploadStatus {
  const next = failUploading(s);
  for (const key of failedKeys) next[key] = 'failed';
  return next;
}

export const pendingCount = (s: UploadStatus): number => Object.keys(s).length;

export const failedCount = (s: UploadStatus): number =>
  Object.values(s).filter((v) => v === 'failed').length;

export function photoState(
  photo: Pick<PhotoRow, 'storage_key'>,
  upload: QueueState | undefined,
): PhotoState {
  if (readMedia(photo.storage_key)) return 'uploaded';
  return upload ?? 'missing';
}
